import { z } from "zod";

export const PORTFOLIO_CONSTRUCTION_PLAN_V2_SCHEMA_VERSION =
  "portfolio_construction_plan.v2" as const;

export const PORTFOLIO_CONSTRUCTION_PLAN_V2_TOTAL_BPS = 10_000 as const;

const PositiveIntSchema = z.number().int().positive();
const NonNegativeIntSchema = z.number().int().nonnegative();
const BpsSchema = z
  .number()
  .int()
  .min(0)
  .max(PORTFOLIO_CONSTRUCTION_PLAN_V2_TOTAL_BPS);
const NonEmptyStringSchema = z.string().trim().min(1);

export const PortfolioConstructionSourceSpanV2Schema = z.object({
  endOffset: NonNegativeIntSchema,
  messageId: NonEmptyStringSchema,
  startOffset: NonNegativeIntSchema,
  text: NonEmptyStringSchema,
});

export const PortfolioConstructionModelProvenanceV2Schema = z.object({
  model: NonEmptyStringSchema,
  provider: NonEmptyStringSchema,
  reasoningEffort: NonEmptyStringSchema.nullable().optional(),
});

export const PortfolioConstructionPlanSourceV2Schema = z.object({
  conversationId: NonEmptyStringSchema,
  model: PortfolioConstructionModelProvenanceV2Schema.nullable().optional(),
  sourceSpans: z.array(PortfolioConstructionSourceSpanV2Schema),
  threadId: NonEmptyStringSchema,
  userMessageIds: z.array(NonEmptyStringSchema).min(1),
});

export const PortfolioConstructionPlanModeV2Schema = z.enum([
  "create",
  "refine",
  "research_then_create",
]);

export const PortfolioConstructionSlotKindV2Schema = z.enum([
  "asset_class",
  "cash_like",
  "category",
  "explicit_exposure",
  "exact_identity",
  "issuer_identity",
  "product_identity",
  "theme",
  "thesis_expression",
]);

export const PortfolioConstructionMentionRelationV2Schema = z.enum([
  "avoid_similar",
  "compare_to",
  "example_of_theme",
  "exclude",
  "include",
  "replace",
]);

export const PortfolioConstructionSlotPriorityV2Schema = z.enum([
  "system_default",
  "user_explicit",
  "user_implied",
]);

export const PortfolioConstructionSlotExpansionPolicyV2Schema = z.enum([
  "equal_split_children",
  "needs_confirmation",
  "single_best",
  "top_n",
  "user_selected_only",
]);

export const PortfolioConstructionSlotStatusV2Schema = z.enum([
  "bound",
  "candidate_searching",
  "candidates_found",
  "needs_review",
  "planned",
  "unresolved",
]);

export const PortfolioConstructionConfidenceV2Schema = z
  .number()
  .min(0)
  .max(1);

export const PortfolioConstructionSlotEvidenceV2Schema = z.object({
  source: z.enum([
    "conversation_context",
    "llm",
    "system_default",
    "user_explicit",
    "user_implied",
  ]),
  sourceSpans: z.array(PortfolioConstructionSourceSpanV2Schema),
  summary: NonEmptyStringSchema,
});

export const PortfolioConstructionIdentityAssetClassHintV2Schema = z.enum([
  "crypto",
  "equity",
  "fund",
  "rwa",
  "stablecoin",
  "tokenized_equity",
]);

export const PortfolioConstructionIdentityEvidenceSourceV2Schema = z.object({
  observedAt: z.string().datetime().nullable().optional(),
  sourceId: NonEmptyStringSchema.nullable().optional(),
  sourceSystem: z.enum([
    "db_v2",
    "eval_fixture",
    "manual",
    "provider",
    "semantic_identity_index",
  ]),
});

export const PortfolioConstructionIdentityEvidenceV2Schema =
  z.discriminatedUnion("kind", [
    z.object({
      field: z.enum([
        "canonicalSymbol",
        "exchangeTicker",
        "productSymbol",
      ]),
      kind: z.literal("exact_symbol_match"),
      matchedSymbol: NonEmptyStringSchema,
      query: NonEmptyStringSchema,
      source: PortfolioConstructionIdentityEvidenceSourceV2Schema,
    }),
    z.object({
      aliasId: NonEmptyStringSchema,
      aliasType: z.enum([
        "curated_common_name",
        "historical_name",
        "product_alias",
        "provider_short_name",
      ]),
      kind: z.literal("exact_alias_match"),
      matchedAlias: NonEmptyStringSchema,
      query: NonEmptyStringSchema,
      source: PortfolioConstructionIdentityEvidenceSourceV2Schema,
    }),
    z.object({
      candidateNameNormalized: NonEmptyStringSchema,
      field: z.enum(["canonicalName", "issuerName", "productName"]),
      kind: z.literal("normalized_full_name_match"),
      queryNormalized: NonEmptyStringSchema,
      source: PortfolioConstructionIdentityEvidenceSourceV2Schema,
    }),
    z.object({
      algorithm: z.enum(["jaro_winkler", "levenshtein", "trigram"]),
      candidateTokenCoverage: z.number().min(0).max(1),
      kind: z.literal("fuzzy_full_phrase_match"),
      matchedNameOrAlias: NonEmptyStringSchema,
      query: NonEmptyStringSchema,
      queryTokenCoverage: z.number().min(0).max(1),
      score: z.number().min(0).max(1),
      source: PortfolioConstructionIdentityEvidenceSourceV2Schema,
    }),
    z.object({
      identifier: NonEmptyStringSchema,
      identifierType: z.enum([
        "cik",
        "cusip",
        "figi",
        "isin",
        "provider_ticker_id",
      ]),
      kind: z.literal("provider_identifier_match"),
      source: PortfolioConstructionIdentityEvidenceSourceV2Schema,
    }),
    z.object({
      kind: z.literal("semantic_identity_recall"),
      score: z.number().min(0).max(1),
      source: PortfolioConstructionIdentityEvidenceSourceV2Schema,
      sufficientForSelection: z.literal(false),
    }),
  ]);

export const PortfolioConstructionIdentityCandidateScoresV2Schema = z.object({
  assetClassCompatibility: z.number().min(0).max(1),
  candidateTokenCoverage: z.number().min(0).max(1),
  exactAlias: z.number().min(0).max(1),
  exactProductSymbol: z.number().min(0).max(1),
  exactSymbol: z.number().min(0).max(1),
  fullPhraseFuzzy: z.number().min(0).max(1),
  normalizedNameExact: z.number().min(0).max(1),
  providerConfidence: z.number().min(0).max(1),
  queryTokenCoverage: z.number().min(0).max(1),
  semanticIdentityRecall: z.number().min(0).max(1),
  themeSimilarity: z.null(),
});

export const PortfolioConstructionIdentityCandidateV2Schema = z.object({
  adjudication: z.object({
    blockedReasons: z.array(NonEmptyStringSchema),
    satisfiesExactIdentity: z.boolean(),
    sufficientEvidenceKinds: z.array(NonEmptyStringSchema),
  }),
  candidateId: NonEmptyStringSchema,
  display: z.object({
    assetClass: PortfolioConstructionIdentityAssetClassHintV2Schema.nullable()
      .optional(),
    name: NonEmptyStringSchema,
    productSymbols: z.array(NonEmptyStringSchema).optional(),
    symbol: NonEmptyStringSchema.nullable().optional(),
  }),
  evidence: z.array(PortfolioConstructionIdentityEvidenceV2Schema),
  scores: PortfolioConstructionIdentityCandidateScoresV2Schema,
  target: z.object({
    canonicalExposureId: NonEmptyStringSchema.nullable().optional(),
    instrumentId: NonEmptyStringSchema.nullable().optional(),
    issuerId: NonEmptyStringSchema.nullable().optional(),
    productId: NonEmptyStringSchema.nullable().optional(),
  }),
});

export const PortfolioConstructionIdentityResolutionDecisionV2Schema =
  z.discriminatedUnion("status", [
    z.object({
      confidence: z.number().min(0).max(1),
      confidenceBand: z.enum(["high", "medium"]),
      reason: NonEmptyStringSchema,
      selectedCandidateId: NonEmptyStringSchema,
      status: z.literal("selected"),
    }),
    z.object({
      candidateIds: z.array(NonEmptyStringSchema).min(1),
      reason: NonEmptyStringSchema,
      status: z.literal("ambiguous"),
    }),
    z.object({
      reason: NonEmptyStringSchema,
      status: z.literal("unresolved"),
    }),
  ]);

export const PortfolioConstructionAssetIdentityResolutionV2Schema = z.object({
  candidates: z.array(PortfolioConstructionIdentityCandidateV2Schema),
  decision: PortfolioConstructionIdentityResolutionDecisionV2Schema,
  diagnostics: z.object({
    retrievalLanesUsed: z.array(
      z.enum([
        "exact_alias",
        "exact_name",
        "exact_symbol",
        "fuzzy_full_phrase",
        "lexical_identity",
        "semantic_identity_recall",
      ]),
    ),
    themeLaneUsed: z.boolean(),
  }),
  indexVersions: z.object({
    aliasTableVersion: NonEmptyStringSchema,
    dbV2SnapshotId: NonEmptyStringSchema,
    identityIndexVersion: NonEmptyStringSchema,
  }),
  normalizedQuery: NonEmptyStringSchema,
  query: NonEmptyStringSchema,
});

export const PortfolioConstructionIdentityMentionV2Schema = z.object({
  assetClassHint: PortfolioConstructionIdentityAssetClassHintV2Schema.nullable()
    .optional(),
  chainHint: PositiveIntSchema.nullable().optional(),
  normalizedText: NonEmptyStringSchema,
  rawText: NonEmptyStringSchema,
  relation: PortfolioConstructionMentionRelationV2Schema,
  resolution: PortfolioConstructionAssetIdentityResolutionV2Schema.nullable()
    .optional(),
  slotKind: z.enum([
    "exact_identity",
    "issuer_identity",
    "product_identity",
  ]),
  sourceSpan: PortfolioConstructionSourceSpanV2Schema,
});

export const PortfolioConstructionBindingCardinalityV2Schema = z
  .object({
    max: PositiveIntSchema,
    min: NonNegativeIntSchema,
    target: PositiveIntSchema,
  })
  .refine((value) => value.min <= value.target, {
    message: "min must be less than or equal to target",
    path: ["min"],
  })
  .refine((value) => value.target <= value.max, {
    message: "target must be less than or equal to max",
    path: ["target"],
  });

export const PortfolioConstructionConstraintScopeV2Schema =
  z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("global") }),
    z.object({ kind: z.literal("slot"), slotId: NonEmptyStringSchema }),
    z.object({
      groupId: NonEmptyStringSchema,
      kind: z.literal("allocation_group"),
    }),
  ]);

export const PortfolioConstructionConstraintOperatorV2Schema = z.enum([
  "avoid",
  "must",
  "prefer",
]);

export const PortfolioConstructionConstraintHardnessV2Schema = z.enum([
  "hard",
  "soft",
]);

export const PortfolioConstructionConstraintSourceV2Schema = z.enum([
  "conversation_context",
  "stored_user_preference",
  "system_default",
  "user_explicit",
  "user_implied",
]);

export const ChainConstraintValueV2Schema = z
  .object({
    allowChainIds: z.array(PositiveIntSchema).optional(),
    appliesTo: z.enum(["all_bindings", "any_binding", "specific_slots"]),
    denyChainIds: z.array(PositiveIntSchema).optional(),
    kind: z.literal("chain"),
  })
  .refine(
    (value) =>
      (value.allowChainIds?.length ?? 0) > 0 ||
      (value.denyChainIds?.length ?? 0) > 0,
    {
      message: "chain constraints must allow or deny at least one chain",
      path: ["allowChainIds"],
    },
  );

export const AssetClassConstraintValueV2Schema = z.object({
  allowAssetClasses: z
    .array(z.enum(["cash", "crypto", "equity", "fund", "other", "rwa"]))
    .min(1),
  kind: z.literal("asset_class"),
});

export const IssuerConstraintValueV2Schema = z.object({
  issuerIds: z.array(NonEmptyStringSchema).min(1),
  kind: z.literal("issuer"),
});

export const ProductConstraintValueV2Schema = z.object({
  kind: z.literal("product"),
  productSymbols: z.array(NonEmptyStringSchema).min(1),
});

export const AssetConstraintValueV2Schema = z.object({
  canonicalExposureIds: z.array(NonEmptyStringSchema).optional(),
  kind: z.literal("asset"),
  symbols: z.array(NonEmptyStringSchema).optional(),
});

export const LiquidityConstraintValueV2Schema = z.object({
  kind: z.literal("liquidity"),
  minLiquidityScore: z.number().min(0).max(1),
});

export const RepresentationConstraintValueV2Schema = z.object({
  allowRepresentationKinds: z
    .array(z.enum(["bridged", "native", "synthetic", "tokenized", "wrapped"]))
    .min(1),
  kind: z.literal("representation_kind"),
});

export const PortfolioConstructionConstraintValueV2Schema =
  z.discriminatedUnion("kind", [
    AssetClassConstraintValueV2Schema,
    AssetConstraintValueV2Schema,
    ChainConstraintValueV2Schema,
    IssuerConstraintValueV2Schema,
    LiquidityConstraintValueV2Schema,
    ProductConstraintValueV2Schema,
    RepresentationConstraintValueV2Schema,
  ]);

export const PortfolioConstructionConstraintTypeV2Schema = z.enum([
  "asset_class",
  "chain",
  "exclude_asset",
  "include_asset",
  "issuer",
  "liquidity",
  "product",
  "region",
  "representation_kind",
  "sector",
  "theme",
  "tradeable_on_glider",
  "wrapper",
]);

export const PortfolioConstructionPlanConstraintV2Schema = z.object({
  confidence: PortfolioConstructionConfidenceV2Schema,
  constraintId: NonEmptyStringSchema,
  hardness: PortfolioConstructionConstraintHardnessV2Schema,
  operator: PortfolioConstructionConstraintOperatorV2Schema,
  scope: PortfolioConstructionConstraintScopeV2Schema,
  source: PortfolioConstructionConstraintSourceV2Schema,
  sourceSpans: z.array(PortfolioConstructionSourceSpanV2Schema),
  type: PortfolioConstructionConstraintTypeV2Schema,
  value: PortfolioConstructionConstraintValueV2Schema,
});

export const PortfolioConstructionPlanSlotV2Schema = z.object({
  allocationRef: NonEmptyStringSchema.nullable().optional(),
  bindingCardinality: PortfolioConstructionBindingCardinalityV2Schema,
  confidence: PortfolioConstructionConfidenceV2Schema,
  constraints: z.array(PortfolioConstructionPlanConstraintV2Schema),
  evidence: z.array(PortfolioConstructionSlotEvidenceV2Schema),
  expansionPolicy: PortfolioConstructionSlotExpansionPolicyV2Schema,
  identity: PortfolioConstructionIdentityMentionV2Schema.nullable().optional(),
  kind: PortfolioConstructionSlotKindV2Schema,
  label: NonEmptyStringSchema,
  priority: PortfolioConstructionSlotPriorityV2Schema,
  candidateSymbols: z.array(NonEmptyStringSchema).optional(),
  query: NonEmptyStringSchema.nullable().optional(),
  requestedCanonicalExposureIds: z.array(NonEmptyStringSchema).optional(),
  requestedSymbols: z.array(NonEmptyStringSchema).optional(),
  required: z.boolean(),
  slotId: NonEmptyStringSchema,
  status: PortfolioConstructionSlotStatusV2Schema,
});

export const PortfolioConstructionAllocationExpressionV2Schema =
  z.discriminatedUnion("kind", [
    z.object({
      bps: BpsSchema,
      expressionId: NonEmptyStringSchema,
      kind: z.literal("exact_bps"),
      originalText: NonEmptyStringSchema,
      slotId: NonEmptyStringSchema,
    }),
    z.object({
      denominator: PositiveIntSchema,
      expressionId: NonEmptyStringSchema,
      kind: z.literal("fraction"),
      numerator: NonNegativeIntSchema,
      originalText: NonEmptyStringSchema,
      slotId: NonEmptyStringSchema,
    }),
    z.object({
      expressionId: NonEmptyStringSchema,
      kind: z.literal("remaining"),
      originalText: NonEmptyStringSchema,
      slotId: NonEmptyStringSchema,
    }),
    z.object({
      expressionId: NonEmptyStringSchema,
      groupTargetBps: BpsSchema.nullable().optional(),
      kind: z.literal("equal_weight_group"),
      originalText: NonEmptyStringSchema,
      slotIds: z.array(NonEmptyStringSchema).min(1),
    }),
  ]);

export const PortfolioConstructionAllocationGroupV2Schema = z.object({
  distribution: z.enum(["equal", "specified"]),
  groupId: NonEmptyStringSchema,
  slotIds: z.array(NonEmptyStringSchema).min(1),
  targetBps: BpsSchema.nullable().optional(),
});

export const PortfolioConstructionAllocationTargetV2Schema = z.object({
  slotId: NonEmptyStringSchema,
  sourceExpressionId: NonEmptyStringSchema,
  targetBps: BpsSchema,
});

export const PortfolioConstructionAllocationSpecV2Schema = z.object({
  expressions: z.array(PortfolioConstructionAllocationExpressionV2Schema),
  groups: z.array(PortfolioConstructionAllocationGroupV2Schema),
  normalizedTargets: z
    .array(PortfolioConstructionAllocationTargetV2Schema)
    .min(1),
  roundingPolicy: z.literal("largest_remainder_bps"),
  totalBps: z.literal(PORTFOLIO_CONSTRUCTION_PLAN_V2_TOTAL_BPS),
});

export const PortfolioConstructionPlanAssumptionV2Schema = z.object({
  assumptionId: NonEmptyStringSchema,
  confidence: PortfolioConstructionConfidenceV2Schema,
  summary: NonEmptyStringSchema,
});

export const PortfolioConstructionPlanValidationSummaryV2Schema = z.object({
  issues: z.array(NonEmptyStringSchema),
  status: z.enum(["fail", "pass", "warn"]),
});

export const PortfolioConstructionPlanProvenanceV2Schema = z.object({
  kind: z.enum(["llm_output", "normalizer_repair", "user_input"]),
  sourceSpans: z.array(PortfolioConstructionSourceSpanV2Schema),
  summary: NonEmptyStringSchema,
});

export const PortfolioConstructionPlanV2Schema = z.object({
  allocation: PortfolioConstructionAllocationSpecV2Schema,
  assumptions: z.array(PortfolioConstructionPlanAssumptionV2Schema),
  constraints: z.array(PortfolioConstructionPlanConstraintV2Schema),
  createdAt: z.string().datetime(),
  mode: PortfolioConstructionPlanModeV2Schema,
  planId: NonEmptyStringSchema,
  planRevision: PositiveIntSchema,
  portfolioName: NonEmptyStringSchema.nullable().optional(),
  provenance: z.array(PortfolioConstructionPlanProvenanceV2Schema),
  schemaVersion: z.literal(PORTFOLIO_CONSTRUCTION_PLAN_V2_SCHEMA_VERSION),
  slots: z.array(PortfolioConstructionPlanSlotV2Schema).min(1),
  source: PortfolioConstructionPlanSourceV2Schema,
  validation: PortfolioConstructionPlanValidationSummaryV2Schema,
});

export const PortfolioConstructionRetrievalTraceV2Schema = z.object({
  lane: z.enum([
    "alias",
    "category",
    "exact_symbol",
    "manual",
    "research",
    "semantic",
    "theme",
  ]),
  query: NonEmptyStringSchema,
  resultCount: NonNegativeIntSchema,
});

export const PortfolioConstructionEvidenceRefV2Schema = z.object({
  evidenceId: NonEmptyStringSchema,
  score: z.number().min(0).max(1),
  source: z.enum([
    "alias",
    "category_membership",
    "external_research",
    "manual",
    "semantic_search",
    "theme_membership",
  ]),
  summary: NonEmptyStringSchema,
});

export const PortfolioConstructionChainProductOptionV2Schema = z.object({
  canonicalExposureId: NonEmptyStringSchema,
  chainToken: z
    .object({
      address: NonEmptyStringSchema.nullable().optional(),
      caip19: NonEmptyStringSchema.nullable().optional(),
      chainId: PositiveIntSchema,
      chainName: NonEmptyStringSchema,
      decimals: NonNegativeIntSchema.nullable().optional(),
      tokenId: NonEmptyStringSchema,
      tokenSymbol: NonEmptyStringSchema,
    })
    .nullable()
    .optional(),
  displayName: NonEmptyStringSchema,
  instrumentId: NonEmptyStringSchema,
  instrumentKind: z.enum([
    "fund",
    "public_equity",
    "rwa",
    "spot_crypto",
    "stablecoin",
    "synthetic",
    "tokenized_equity",
    "wrapped_token",
  ]),
  issuerId: NonEmptyStringSchema.nullable().optional(),
  issuerName: NonEmptyStringSchema.nullable().optional(),
  optionId: NonEmptyStringSchema,
  productId: NonEmptyStringSchema.nullable().optional(),
  productSymbol: NonEmptyStringSchema.nullable().optional(),
  riskFlags: z.array(
    z.enum([
      "bridged_token",
      "issuer_custody_risk",
      "low_liquidity",
      "not_compileable",
      "stale_route_data",
      "synthetic_exposure",
      "wrapped_exposure",
    ]),
  ),
  route: z
    .object({
      estimatedCostBps: z.number().nonnegative().nullable().optional(),
      freshnessTimestamp: z.string().datetime().nullable().optional(),
      isTradeableOnGlider: z.boolean(),
      liquidityScore: z.number().min(0).max(1).nullable().optional(),
      routeId: NonEmptyStringSchema,
      routeQualityScore: z.number().min(0).max(1),
      venue: NonEmptyStringSchema.nullable().optional(),
    })
    .nullable()
    .optional(),
  symbol: NonEmptyStringSchema,
  wrapperId: NonEmptyStringSchema.nullable().optional(),
  wrapperKind: z
    .enum(["bridged", "native", "synthetic", "tokenized", "wrapped"])
    .nullable()
    .optional(),
});

export const PortfolioConstructionCandidateExposureV2Schema = z.object({
  assetClass: z.enum(["cash", "crypto", "equity", "fund", "other", "rwa"]),
  candidateId: NonEmptyStringSchema,
  canonicalExposureId: NonEmptyStringSchema,
  displayName: NonEmptyStringSchema,
  displaySymbol: NonEmptyStringSchema,
  match: z.object({
    aliasMatch: z.boolean(),
    categoryEvidenceScore: z.number().min(0).max(1),
    exactSymbol: z.boolean(),
    semanticScore: z.number().min(0).max(1).nullable().optional(),
    sourceEvidence: z.array(PortfolioConstructionEvidenceRefV2Schema),
  }),
  options: z.array(PortfolioConstructionChainProductOptionV2Schema),
  ranking: z.object({
    categoryPrecisionScore: z.number().min(0).max(1).nullable().optional(),
    exposureMatchScore: z.number().min(0).max(1),
    freshnessScore: z.number().min(0).max(1).nullable().optional(),
    liquidityScore: z.number().min(0).max(1).nullable().optional(),
    totalPreOptimizationScore: z.number(),
    tradeabilityScore: z.number().min(0).max(1).nullable().optional(),
  }),
  slotId: NonEmptyStringSchema,
});

export const PortfolioConstructionSlotCandidateSetV2Schema = z.object({
  assetGraphSnapshotId: NonEmptyStringSchema,
  candidates: z.array(PortfolioConstructionCandidateExposureV2Schema),
  retrievalTrace: z.array(PortfolioConstructionRetrievalTraceV2Schema),
  slotId: NonEmptyStringSchema,
  status: z.enum([
    "blocked_by_constraints",
    "empty",
    "low_confidence",
    "needs_review",
    "ok",
  ]),
});

export const PortfolioConstructionOptimizerScoreBreakdownV2Schema = z.object({
  categoryPrecisionScore: z.number(),
  exposureMatchScore: z.number(),
  oneChainScore: z.number(),
  requestedChainScore: z.number(),
  reviewWarningPenalty: z.number(),
  riskPenalty: z.number(),
  routeQualityScore: z.number(),
  totalScore: z.number(),
  tradeabilityScore: z.number(),
});

export const PortfolioConstructionAssetBindingV2Schema = z.object({
  bindingId: NonEmptyStringSchema,
  candidateId: NonEmptyStringSchema,
  canonicalExposureId: NonEmptyStringSchema,
  confidence: PortfolioConstructionConfidenceV2Schema,
  planId: NonEmptyStringSchema,
  planRevision: PositiveIntSchema,
  satisfiedConstraintIds: z.array(NonEmptyStringSchema),
  scoreBreakdown: PortfolioConstructionOptimizerScoreBreakdownV2Schema,
  selectedOptionId: NonEmptyStringSchema,
  selectedRepresentation: z.object({
    chainId: PositiveIntSchema.nullable().optional(),
    issuerId: NonEmptyStringSchema.nullable().optional(),
    productId: NonEmptyStringSchema.nullable().optional(),
    productSymbol: NonEmptyStringSchema.nullable().optional(),
    routeId: NonEmptyStringSchema.nullable().optional(),
    tokenId: NonEmptyStringSchema.nullable().optional(),
  }),
  selectionReason: NonEmptyStringSchema,
  slotId: NonEmptyStringSchema,
  sourceAllocationExpressionId: NonEmptyStringSchema,
  status: z.enum(["manual_locked", "needs_review", "selected", "unresolved"]),
  targetBps: BpsSchema,
  violatedConstraintIds: z.array(NonEmptyStringSchema),
  warnings: z.array(NonEmptyStringSchema),
});

export const PortfolioConstructionVerificationCheckV2Schema = z.object({
  checkId: NonEmptyStringSchema,
  message: NonEmptyStringSchema,
  relatedBindingIds: z.array(NonEmptyStringSchema).optional(),
  relatedSlotIds: z.array(NonEmptyStringSchema).optional(),
  status: z.enum(["fail", "pass", "warn"]),
  type: z.enum([
    "allocation_sum",
    "asset_class_constraint",
    "chain_constraint",
    "duplicate_exposure",
    "identity_evidence",
    "low_confidence",
    "product_constraint",
    "required_slot_bound",
    "slot_allocation",
    "tradeability",
  ]),
});

export const PortfolioConstructionVerificationResultV2Schema = z.object({
  checks: z.array(PortfolioConstructionVerificationCheckV2Schema),
  status: z.enum(["fail", "needs_review", "pass"]),
});

export const PortfolioConstructionScenarioExpectationV2Schema = z.object({
  expectedBindings: z
    .array(
      z.object({
        canonicalExposureId: NonEmptyStringSchema,
        chainId: PositiveIntSchema.nullable().optional(),
        productSymbol: NonEmptyStringSchema.nullable().optional(),
        slotId: NonEmptyStringSchema,
        targetBps: BpsSchema,
      }),
    )
    .optional(),
  expectedPlan: z
    .object({
      chainAllowIds: z.array(PositiveIntSchema).optional(),
      chainDenyIds: z.array(PositiveIntSchema).optional(),
      requiredSymbols: z.array(NonEmptyStringSchema).optional(),
      targetBpsBySlotId: z.record(NonEmptyStringSchema, BpsSchema).optional(),
    })
    .optional(),
  forbidden: z
    .object({
      canonicalExposureIds: z.array(NonEmptyStringSchema).optional(),
      chainIds: z.array(PositiveIntSchema).optional(),
      productSymbols: z.array(NonEmptyStringSchema).optional(),
      symbols: z.array(NonEmptyStringSchema).optional(),
    })
    .optional(),
});

export const PortfolioConstructionScenarioTurnV2Schema = z.object({
  expectation: PortfolioConstructionScenarioExpectationV2Schema.optional(),
  userText: NonEmptyStringSchema,
});

export const PortfolioConstructionScenarioV2Schema = z.object({
  assetGraphFixtureId: NonEmptyStringSchema.nullable().optional(),
  scenarioId: NonEmptyStringSchema,
  tags: z.array(NonEmptyStringSchema),
  turns: z.array(PortfolioConstructionScenarioTurnV2Schema).min(1),
});

export type PortfolioConstructionPlanV2 = z.infer<
  typeof PortfolioConstructionPlanV2Schema
>;
export type PortfolioConstructionPlanSlotV2 = z.infer<
  typeof PortfolioConstructionPlanSlotV2Schema
>;
export type PortfolioConstructionPlanConstraintV2 = z.infer<
  typeof PortfolioConstructionPlanConstraintV2Schema
>;
export type PortfolioConstructionSlotCandidateSetV2 = z.infer<
  typeof PortfolioConstructionSlotCandidateSetV2Schema
>;
export type PortfolioConstructionCandidateExposureV2 = z.infer<
  typeof PortfolioConstructionCandidateExposureV2Schema
>;
export type PortfolioConstructionChainProductOptionV2 = z.infer<
  typeof PortfolioConstructionChainProductOptionV2Schema
>;
export type PortfolioConstructionAssetBindingV2 = z.infer<
  typeof PortfolioConstructionAssetBindingV2Schema
>;
export type PortfolioConstructionVerificationResultV2 = z.infer<
  typeof PortfolioConstructionVerificationResultV2Schema
>;
export type PortfolioConstructionVerificationCheckV2 = z.infer<
  typeof PortfolioConstructionVerificationCheckV2Schema
>;
export type PortfolioConstructionScenarioV2 = z.infer<
  typeof PortfolioConstructionScenarioV2Schema
>;
export type PortfolioConstructionIdentityEvidenceV2 = z.infer<
  typeof PortfolioConstructionIdentityEvidenceV2Schema
>;
export type PortfolioConstructionIdentityAssetClassHintV2 = z.infer<
  typeof PortfolioConstructionIdentityAssetClassHintV2Schema
>;
export type PortfolioConstructionIdentityCandidateV2 = z.infer<
  typeof PortfolioConstructionIdentityCandidateV2Schema
>;
export type PortfolioConstructionAssetIdentityResolutionV2 = z.infer<
  typeof PortfolioConstructionAssetIdentityResolutionV2Schema
>;
export type PortfolioConstructionIdentityMentionV2 = z.infer<
  typeof PortfolioConstructionIdentityMentionV2Schema
>;

export interface PortfolioConstructionPlanValidationIssueV2 {
  readonly code:
    | "allocation_total_mismatch"
    | "binding_cardinality_invalid"
    | "duplicate_allocation_target"
    | "missing_required_slot_allocation"
    | "slot_reference_missing";
  readonly message: string;
  readonly slotId?: string;
}

export interface PortfolioConstructionPlanInvariantResultV2 {
  readonly issues: readonly PortfolioConstructionPlanValidationIssueV2[];
  readonly status: "fail" | "pass";
}

export interface PortfolioConstructionIdentityVerificationIssueV2 {
  readonly candidateId?: string;
  readonly code:
    | "ambiguous_identity"
    | "insufficient_identity_evidence"
    | "missing_selected_candidate"
    | "theme_lane_used"
    | "unresolved_identity";
  readonly message: string;
}

export interface PortfolioConstructionIdentityVerificationResultV2 {
  readonly issues: readonly PortfolioConstructionIdentityVerificationIssueV2[];
  readonly status: "fail" | "pass";
}

const IDENTITY_EVIDENCE_SUFFICIENT_FUZZY_SCORE = 0.88;
const IDENTITY_EVIDENCE_SUFFICIENT_QUERY_TOKEN_COVERAGE = 0.9;

export function isPortfolioConstructionIdentityEvidenceSufficientV2(
  evidence: PortfolioConstructionIdentityEvidenceV2,
): boolean {
  switch (evidence.kind) {
    case "exact_alias_match":
    case "exact_symbol_match":
    case "normalized_full_name_match":
    case "provider_identifier_match":
      return true;
    case "fuzzy_full_phrase_match":
      return (
        evidence.score >= IDENTITY_EVIDENCE_SUFFICIENT_FUZZY_SCORE &&
        evidence.queryTokenCoverage >=
          IDENTITY_EVIDENCE_SUFFICIENT_QUERY_TOKEN_COVERAGE
      );
    case "semantic_identity_recall":
      return false;
  }
}

export function listSufficientPortfolioConstructionIdentityEvidenceKindsV2(
  evidence: readonly PortfolioConstructionIdentityEvidenceV2[],
): string[] {
  return [
    ...new Set(
      evidence
        .filter(isPortfolioConstructionIdentityEvidenceSufficientV2)
        .map((item) => item.kind),
    ),
  ];
}

export function verifyPortfolioConstructionIdentityResolutionV2(
  resolution: PortfolioConstructionAssetIdentityResolutionV2,
): PortfolioConstructionIdentityVerificationResultV2 {
  const issues: PortfolioConstructionIdentityVerificationIssueV2[] = [];

  if (resolution.diagnostics.themeLaneUsed) {
    issues.push({
      code: "theme_lane_used",
      message: "Exact identity resolution cannot use theme lane authority",
    });
  }

  if (resolution.decision.status === "unresolved") {
    issues.push({
      code: "unresolved_identity",
      message: resolution.decision.reason,
    });
  }

  if (resolution.decision.status === "ambiguous") {
    issues.push({
      code: "ambiguous_identity",
      message: resolution.decision.reason,
    });
  }

  if (resolution.decision.status === "selected") {
    const selectedCandidateId = resolution.decision.selectedCandidateId;
    const selectedCandidate =
      resolution.candidates.find(
        (candidate) => candidate.candidateId === selectedCandidateId,
      ) ?? null;
    if (!selectedCandidate) {
      issues.push({
        candidateId: selectedCandidateId,
        code: "missing_selected_candidate",
        message: `Selected identity candidate ${selectedCandidateId} was not returned`,
      });
    } else {
      const sufficientEvidenceKinds =
        listSufficientPortfolioConstructionIdentityEvidenceKindsV2(
          selectedCandidate.evidence,
        );
      if (sufficientEvidenceKinds.length === 0) {
        issues.push({
          candidateId: selectedCandidate.candidateId,
          code: "insufficient_identity_evidence",
          message: `Selected identity candidate ${selectedCandidate.candidateId} has no binding identity evidence`,
        });
      }
    }
  }

  return {
    issues,
    status: issues.length === 0 ? "pass" : "fail",
  };
}

export function validatePortfolioConstructionPlanV2Invariants(
  plan: PortfolioConstructionPlanV2,
): PortfolioConstructionPlanInvariantResultV2 {
  const issues: PortfolioConstructionPlanValidationIssueV2[] = [];
  const slotIds = new Set(plan.slots.map((slot) => slot.slotId));
  const allocationSlotIds = new Set<string>();
  let totalBps = 0;

  for (const target of plan.allocation.normalizedTargets) {
    totalBps += target.targetBps;
    if (!slotIds.has(target.slotId)) {
      issues.push({
        code: "slot_reference_missing",
        message: `Allocation target references missing slot ${target.slotId}`,
        slotId: target.slotId,
      });
    }
    if (allocationSlotIds.has(target.slotId)) {
      issues.push({
        code: "duplicate_allocation_target",
        message: `Slot ${target.slotId} has more than one normalized target`,
        slotId: target.slotId,
      });
    }
    allocationSlotIds.add(target.slotId);
  }

  if (totalBps !== PORTFOLIO_CONSTRUCTION_PLAN_V2_TOTAL_BPS) {
    issues.push({
      code: "allocation_total_mismatch",
      message: `Allocation targets sum to ${totalBps} bps, expected ${PORTFOLIO_CONSTRUCTION_PLAN_V2_TOTAL_BPS}`,
    });
  }

  for (const slot of plan.slots) {
    if (
      slot.bindingCardinality.min > slot.bindingCardinality.target ||
      slot.bindingCardinality.target > slot.bindingCardinality.max
    ) {
      issues.push({
        code: "binding_cardinality_invalid",
        message: `Slot ${slot.slotId} has invalid binding cardinality`,
        slotId: slot.slotId,
      });
    }

    if (slot.required && !allocationSlotIds.has(slot.slotId)) {
      issues.push({
        code: "missing_required_slot_allocation",
        message: `Required slot ${slot.slotId} is missing a normalized allocation target`,
        slotId: slot.slotId,
      });
    }
  }

  return {
    issues,
    status: issues.length === 0 ? "pass" : "fail",
  };
}

export interface VerifyPortfolioConstructionBindingsV2Input {
  readonly bindings: readonly PortfolioConstructionAssetBindingV2[];
  readonly plan: PortfolioConstructionPlanV2;
}

export function verifyPortfolioConstructionBindingsV2({
  bindings,
  plan,
}: VerifyPortfolioConstructionBindingsV2Input): PortfolioConstructionVerificationResultV2 {
  const checks: PortfolioConstructionVerificationCheckV2[] = [];
  const bindingsBySlotId = new Map<string, PortfolioConstructionAssetBindingV2[]>();
  const requiredSlots = plan.slots.filter((slot) => slot.required);

  for (const binding of bindings) {
    const existing = bindingsBySlotId.get(binding.slotId) ?? [];
    bindingsBySlotId.set(binding.slotId, [...existing, binding]);
  }

  const totalBps = bindings.reduce((sum, binding) => sum + binding.targetBps, 0);
  checks.push({
    checkId: "allocation_sum",
    message:
      totalBps === PORTFOLIO_CONSTRUCTION_PLAN_V2_TOTAL_BPS
        ? "Selected bindings sum to 10,000 bps"
        : `Selected bindings sum to ${totalBps} bps`,
    status:
      totalBps === PORTFOLIO_CONSTRUCTION_PLAN_V2_TOTAL_BPS ? "pass" : "fail",
    type: "allocation_sum",
  });

  for (const target of plan.allocation.normalizedTargets) {
    const slotBindings = bindingsBySlotId.get(target.slotId) ?? [];
    const selectedSlotBps = slotBindings
      .filter(
        (binding) =>
          binding.status === "selected" || binding.status === "manual_locked",
      )
      .reduce((sum, binding) => sum + binding.targetBps, 0);
    const status = selectedSlotBps === target.targetBps ? "pass" : "fail";
    checks.push({
      checkId: `slot_allocation:${target.slotId}`,
      message:
        status === "pass"
          ? `Slot ${target.slotId} selected bindings match ${target.targetBps} bps`
          : `Slot ${target.slotId} selected bindings sum to ${selectedSlotBps} bps, expected ${target.targetBps}`,
      relatedBindingIds: slotBindings.map((binding) => binding.bindingId),
      relatedSlotIds: [target.slotId],
      status,
      type: "slot_allocation",
    });
  }

  for (const slot of requiredSlots) {
    const slotBindings = bindingsBySlotId.get(slot.slotId) ?? [];
    const selectedCount = slotBindings.filter(
      (binding) =>
        binding.status === "selected" || binding.status === "manual_locked",
    ).length;
    const status =
      selectedCount >= slot.bindingCardinality.min &&
      selectedCount <= slot.bindingCardinality.max
        ? "pass"
        : "fail";
    checks.push({
      checkId: `required_slot_bound:${slot.slotId}`,
      message:
        status === "pass"
          ? `Required slot ${slot.slotId} is bound`
          : `Required slot ${slot.slotId} has ${selectedCount} bindings`,
      relatedSlotIds: [slot.slotId],
      status,
      type: "required_slot_bound",
    });
  }

  for (const slot of requiredSlots) {
    if (!isIdentityVerificationRequiredForSlot(slot)) {
      continue;
    }

    const resolution = slot.identity?.resolution ?? null;
    if (!resolution) {
      checks.push({
        checkId: `identity_evidence:${slot.slotId}`,
        message: `Required identity slot ${slot.slotId} is missing resolver evidence`,
        relatedSlotIds: [slot.slotId],
        status: "fail",
        type: "identity_evidence",
      });
      continue;
    }

    const verification = verifyPortfolioConstructionIdentityResolutionV2(
      resolution,
    );
    checks.push({
      checkId: `identity_evidence:${slot.slotId}`,
      message:
        verification.status === "pass"
          ? `Required identity slot ${slot.slotId} has sufficient resolver evidence`
          : verification.issues.map((issue) => issue.message).join("; "),
      relatedSlotIds: [slot.slotId],
      status: verification.status,
      type: "identity_evidence",
    });
  }

  const exposureCounts = new Map<string, number>();
  for (const binding of bindings) {
    exposureCounts.set(
      binding.canonicalExposureId,
      (exposureCounts.get(binding.canonicalExposureId) ?? 0) + 1,
    );
  }
  for (const [canonicalExposureId, count] of exposureCounts.entries()) {
    if (count > 1) {
      checks.push({
        checkId: `duplicate_exposure:${canonicalExposureId}`,
        message: `Canonical exposure ${canonicalExposureId} is selected ${count} times`,
        status: "fail",
        type: "duplicate_exposure",
      });
    }
  }
  if (![...exposureCounts.values()].some((count) => count > 1)) {
    checks.push({
      checkId: "duplicate_exposure",
      message: "No duplicate canonical exposures",
      status: "pass",
      type: "duplicate_exposure",
    });
  }

  const hardChainConstraints = readHardChainConstraints(plan);
  for (const constraint of hardChainConstraints) {
    const scopedBindings = bindings.filter((binding) =>
      constraintAppliesToBinding(constraint, binding),
    );
    const failedBindings = scopedBindings.filter((binding) => {
      const chainId = binding.selectedRepresentation.chainId ?? null;
      if (chainId === null) {
        return true;
      }
      const allowChainIds = constraint.value.allowChainIds ?? [];
      const denyChainIds = constraint.value.denyChainIds ?? [];
      return (
        (allowChainIds.length > 0 && !allowChainIds.includes(chainId)) ||
        denyChainIds.includes(chainId)
      );
    });
    checks.push({
      checkId: `chain_constraint:${constraint.constraintId}`,
      message:
        failedBindings.length === 0
          ? `Hard chain constraint ${constraint.constraintId} passed`
          : `Hard chain constraint ${constraint.constraintId} failed for ${failedBindings.length} bindings`,
      relatedBindingIds: failedBindings.map((binding) => binding.bindingId),
      status: failedBindings.length === 0 ? "pass" : "fail",
      type: "chain_constraint",
    });
  }

  const hardProductConstraints = readHardProductConstraints(plan);
  for (const constraint of hardProductConstraints) {
    const scopedBindings = bindings.filter((binding) =>
      constraintAppliesToBinding(constraint, binding),
    );
    const failedBindings = scopedBindings.filter(
      (binding) => !bindingSatisfiesProductConstraint(binding, constraint),
    );
    checks.push({
      checkId: `product_constraint:${constraint.constraintId}`,
      message:
        failedBindings.length === 0
          ? `Hard product constraint ${constraint.constraintId} passed`
          : `Hard product constraint ${constraint.constraintId} failed for ${failedBindings.length} bindings`,
      relatedBindingIds: failedBindings.map((binding) => binding.bindingId),
      status: failedBindings.length === 0 ? "pass" : "fail",
      type: "product_constraint",
    });
  }

  for (const binding of bindings) {
    const isTradeable = Boolean(binding.selectedRepresentation.routeId);
    checks.push({
      checkId: `tradeability:${binding.bindingId}`,
      message: isTradeable
        ? `Binding ${binding.bindingId} has a compileable route`
        : `Binding ${binding.bindingId} is missing a route`,
      relatedBindingIds: [binding.bindingId],
      relatedSlotIds: [binding.slotId],
      status: isTradeable ? "pass" : "fail",
      type: "tradeability",
    });
  }

  const hasFailures = checks.some((check) => check.status === "fail");
  const hasWarnings = checks.some((check) => check.status === "warn");

  return {
    checks,
    status: hasFailures ? "fail" : hasWarnings ? "needs_review" : "pass",
  };
}

function isIdentityVerificationRequiredForSlot(
  slot: PortfolioConstructionPlanSlotV2,
): boolean {
  return (
    ["exact_identity", "issuer_identity", "product_identity"].includes(
      slot.kind,
    ) || slot.identity?.resolution != null
  );
}

function readHardChainConstraints(
  plan: PortfolioConstructionPlanV2,
): Array<
  PortfolioConstructionPlanConstraintV2 & {
    readonly value: z.infer<typeof ChainConstraintValueV2Schema>;
  }
> {
  return readPlanAndSlotConstraints(plan).filter(
    (
      constraint,
    ): constraint is PortfolioConstructionPlanConstraintV2 & {
      readonly value: z.infer<typeof ChainConstraintValueV2Schema>;
    } =>
      constraint.type === "chain" &&
      constraint.hardness === "hard" &&
      constraint.value.kind === "chain",
  );
}

function readHardProductConstraints(
  plan: PortfolioConstructionPlanV2,
): Array<
  PortfolioConstructionPlanConstraintV2 & {
    readonly operator: "avoid" | "must";
    readonly value: z.infer<typeof ProductConstraintValueV2Schema>;
  }
> {
  return readPlanAndSlotConstraints(plan).filter(
    (
      constraint,
    ): constraint is PortfolioConstructionPlanConstraintV2 & {
      readonly operator: "avoid" | "must";
      readonly value: z.infer<typeof ProductConstraintValueV2Schema>;
    } =>
      constraint.type === "product" &&
      constraint.hardness === "hard" &&
      (constraint.operator === "must" || constraint.operator === "avoid") &&
      constraint.value.kind === "product",
  );
}

function readPlanAndSlotConstraints(
  plan: PortfolioConstructionPlanV2,
): PortfolioConstructionPlanConstraintV2[] {
  return [
    ...plan.constraints,
    ...plan.slots.flatMap((slot) => slot.constraints),
  ];
}

function constraintAppliesToBinding(
  constraint: PortfolioConstructionPlanConstraintV2,
  binding: PortfolioConstructionAssetBindingV2,
): boolean {
  if (constraint.scope.kind === "global") {
    return true;
  }
  if (constraint.scope.kind === "slot") {
    return constraint.scope.slotId === binding.slotId;
  }
  return true;
}

function normalizeProductSymbol(symbol: string | null | undefined): string {
  return symbol?.trim().toUpperCase() ?? "";
}

function bindingSatisfiesProductConstraint(
  binding: PortfolioConstructionAssetBindingV2,
  constraint: ReturnType<typeof readHardProductConstraints>[number],
): boolean {
  const selectedProductSymbol = normalizeProductSymbol(
    binding.selectedRepresentation.productSymbol,
  );
  const productSymbols = new Set(
    constraint.value.productSymbols.map(normalizeProductSymbol),
  );

  if (constraint.operator === "must") {
    return (
      selectedProductSymbol.length > 0 &&
      productSymbols.has(selectedProductSymbol)
    );
  }

  return !productSymbols.has(selectedProductSymbol);
}

export const PORTFOLIO_CONSTRUCTION_V2_GOLDEN_SCENARIOS = [
  {
    assetGraphFixtureId: "eth-btc-stables-base-ethereum",
    scenarioId: "eth-btc-usdc-ethereum-fractions",
    tags: ["allocation", "chain", "exact_assets", "optimizer"],
    turns: [
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "crypto:eth",
              chainId: 1,
              productSymbol: "ETH",
              slotId: "slot_eth",
              targetBps: 2500,
            },
            {
              canonicalExposureId: "crypto:btc",
              chainId: 1,
              productSymbol: "WBTC",
              slotId: "slot_btc",
              targetBps: 2500,
            },
            {
              canonicalExposureId: "crypto:usdc",
              chainId: 1,
              productSymbol: "USDC",
              slotId: "slot_usdc",
              targetBps: 5000,
            },
          ],
          expectedPlan: {
            chainAllowIds: [1],
            requiredSymbols: ["ETH", "BTC", "USDC"],
            targetBpsBySlotId: {
              slot_btc: 2500,
              slot_eth: 2500,
              slot_usdc: 5000,
            },
          },
        },
        userText:
          "Create a portfolio with 1/4 ETH, 1/4 BTC, and the rest USDC on ethereum.",
      },
    ],
  },
  {
    assetGraphFixtureId: "eth-btc-stables-base-ethereum",
    scenarioId: "eth-btc-usdc-base-fractions",
    tags: ["allocation", "chain", "exact_assets", "optimizer"],
    turns: [
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "crypto:eth",
              chainId: 8453,
              productSymbol: "ETH",
              slotId: "slot_eth",
              targetBps: 2500,
            },
            {
              canonicalExposureId: "crypto:btc",
              chainId: 8453,
              productSymbol: "cbBTC",
              slotId: "slot_btc",
              targetBps: 2500,
            },
            {
              canonicalExposureId: "crypto:usdc",
              chainId: 8453,
              productSymbol: "USDC",
              slotId: "slot_usdc",
              targetBps: 5000,
            },
          ],
          expectedPlan: {
            chainAllowIds: [8453],
            requiredSymbols: ["ETH", "BTC", "USDC"],
            targetBpsBySlotId: {
              slot_btc: 2500,
              slot_eth: 2500,
              slot_usdc: 5000,
            },
          },
        },
        userText:
          "Create a portfolio with 1/4 ETH, 1/4 BTC, and the rest USDC on Base.",
      },
    ],
  },
  {
    assetGraphFixtureId: "eth-degen-stables",
    scenarioId: "eth-stables-degen-weighted",
    tags: ["allocation", "exact_assets", "stablecoins"],
    turns: [
      {
        expectation: {
          expectedPlan: {
            requiredSymbols: ["ETH", "USDC", "USDT", "DEGEN"],
            targetBpsBySlotId: {
              slot_degen: 1000,
              slot_eth: 6000,
              slot_usdc: 1500,
              slot_usdt: 1500,
            },
          },
        },
        userText:
          "Create a diversified portfolio with 60% ETH, 30% stablecoins split equally between USDC and USDT, and 10% DEGEN.",
      },
    ],
  },
  {
    assetGraphFixtureId: "eth-btc-stables-base-ethereum",
    scenarioId: "usdc-usdt-dai-ethereum-not-base",
    tags: ["allocation", "chain", "exact_assets", "stablecoins"],
    turns: [
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "crypto:usdc",
              chainId: 1,
              productSymbol: "USDC",
              slotId: "slot_usdc",
              targetBps: 3334,
            },
            {
              canonicalExposureId: "crypto:usdt",
              chainId: 1,
              productSymbol: "USDT",
              slotId: "slot_usdt",
              targetBps: 3333,
            },
            {
              canonicalExposureId: "crypto:dai",
              chainId: 1,
              productSymbol: "DAI",
              slotId: "slot_dai",
              targetBps: 3333,
            },
          ],
          expectedPlan: {
            chainAllowIds: [1],
            chainDenyIds: [8453],
            requiredSymbols: ["USDC", "USDT", "DAI"],
            targetBpsBySlotId: {
              slot_dai: 3333,
              slot_usdc: 3334,
              slot_usdt: 3333,
            },
          },
        },
        userText:
          "I want an equally weighted USDC/USDT/DAI portfolio on ethereum not base",
      },
    ],
  },
  {
    assetGraphFixtureId: "eth-btc-stables-base-ethereum",
    scenarioId: "explicit-wbtc-product-ethereum",
    tags: ["exact_assets", "optimizer", "product"],
    turns: [
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "crypto:btc",
              chainId: 1,
              productSymbol: "WBTC",
              slotId: "slot_wbtc",
              targetBps: 10000,
            },
          ],
          expectedPlan: {
            chainAllowIds: [1],
            requiredSymbols: ["WBTC"],
            targetBpsBySlotId: {
              slot_wbtc: 10000,
            },
          },
        },
        userText: "Create a WBTC portfolio on Ethereum.",
      },
    ],
  },
  {
    assetGraphFixtureId: "eth-btc-stables-base-ethereum",
    scenarioId: "explicit-cbbtc-product-base",
    tags: ["chain", "exact_assets", "optimizer", "product"],
    turns: [
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "crypto:btc",
              chainId: 8453,
              productSymbol: "cbBTC",
              slotId: "slot_cbbtc",
              targetBps: 5000,
            },
            {
              canonicalExposureId: "crypto:usdc",
              chainId: 8453,
              productSymbol: "USDC",
              slotId: "slot_usdc",
              targetBps: 5000,
            },
          ],
          expectedPlan: {
            chainAllowIds: [8453],
            requiredSymbols: ["cbBTC", "USDC"],
            targetBpsBySlotId: {
              slot_cbbtc: 5000,
              slot_usdc: 5000,
            },
          },
        },
        userText: "Create an equal-weight portfolio of cbBTC and USDC on Base.",
      },
    ],
  },
  {
    assetGraphFixtureId: "eth-btc-stables-base-ethereum",
    scenarioId: "refine-cbbtc-base-to-mainnet-btc-exposure",
    tags: ["chain", "exact_assets", "optimizer", "product", "refinement"],
    turns: [
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "crypto:btc",
              chainId: 8453,
              productSymbol: "cbBTC",
              slotId: "slot_cbbtc",
              targetBps: 10000,
            },
          ],
          expectedPlan: {
            chainAllowIds: [8453],
            requiredSymbols: ["cbBTC"],
            targetBpsBySlotId: {
              slot_cbbtc: 10000,
            },
          },
        },
        userText: "Make a portfolio of cbBTC.",
      },
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "crypto:btc",
              chainId: 1,
              productSymbol: "WBTC",
              slotId: "slot_btc",
              targetBps: 10000,
            },
          ],
          expectedPlan: {
            chainAllowIds: [1],
            requiredSymbols: ["BTC"],
            targetBpsBySlotId: {
              slot_btc: 10000,
            },
          },
        },
        userText: "no, on mainnet",
      },
    ],
  },
  {
    assetGraphFixtureId: "eth-btc-stables-base-ethereum",
    scenarioId: "refine-btc-base-to-wbtc-mainnet-product",
    tags: ["chain", "exact_assets", "optimizer", "product", "refinement"],
    turns: [
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "crypto:btc",
              chainId: 8453,
              productSymbol: "cbBTC",
              slotId: "slot_btc",
              targetBps: 10000,
            },
          ],
          expectedPlan: {
            chainAllowIds: [8453],
            requiredSymbols: ["BTC"],
            targetBpsBySlotId: {
              slot_btc: 10000,
            },
          },
        },
        userText: "Make a portfolio of BTC on Base.",
      },
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "crypto:btc",
              chainId: 1,
              productSymbol: "WBTC",
              slotId: "slot_wbtc",
              targetBps: 10000,
            },
          ],
          expectedPlan: {
            chainAllowIds: [1],
            requiredSymbols: ["WBTC"],
            targetBpsBySlotId: {
              slot_wbtc: 10000,
            },
          },
        },
        userText: "actually use WBTC on mainnet instead",
      },
    ],
  },
  {
    assetGraphFixtureId: "rwa-equity-identity",
    scenarioId: "symbol-ko-tsla",
    tags: ["equity", "exact_identity", "identity", "symbol_resolution"],
    turns: [
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "equity:ko",
              chainId: 1,
              productSymbol: "KOon",
              slotId: "slot_ko",
              targetBps: 5000,
            },
            {
              canonicalExposureId: "equity:tsla",
              chainId: 1,
              productSymbol: "TSLAon",
              slotId: "slot_tsla",
              targetBps: 5000,
            },
          ],
          expectedPlan: {
            requiredSymbols: ["KO", "TSLA"],
            targetBpsBySlotId: {
              slot_ko: 5000,
              slot_tsla: 5000,
            },
          },
        },
        userText: "Create a portfolio with KO and TSLA",
      },
    ],
  },
  {
    assetGraphFixtureId: "rwa-equity-identity",
    scenarioId: "full-name-lockheed-tsla",
    tags: ["equity", "exact_identity", "identity", "name_resolution"],
    turns: [
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "equity:lmt",
              chainId: 1,
              productSymbol: "LMTon",
              slotId: "slot_lockheed_martin",
              targetBps: 5000,
            },
            {
              canonicalExposureId: "equity:tsla",
              chainId: 1,
              productSymbol: "TSLAon",
              slotId: "slot_tsla",
              targetBps: 5000,
            },
          ],
          expectedPlan: {
            requiredSymbols: ["LMT", "TSLA"],
            targetBpsBySlotId: {
              slot_lockheed_martin: 5000,
              slot_tsla: 5000,
            },
          },
          forbidden: {
            symbols: ["GOOG", "GOOGL"],
          },
        },
        userText: "Create an equal-weight portfolio of Lockheed Martin and TSLA",
      },
    ],
  },
  {
    assetGraphFixtureId: "rwa-equity-identity",
    scenarioId: "misspelled-defense-company-names",
    tags: ["equity", "exact_identity", "identity", "typo_tolerance"],
    turns: [
      {
        expectation: {
          expectedPlan: {
            requiredSymbols: ["LMT", "NOC"],
            targetBpsBySlotId: {
              slot_lockhed_martin: 5000,
              slot_northrop_gruman: 5000,
            },
          },
          forbidden: {
            symbols: ["GOOG", "GOOGL", "MLM"],
          },
        },
        userText: "create a portfolio of lockhed martin and northrop gruman",
      },
    ],
  },
  {
    assetGraphFixtureId: "rwa-equity-identity-and-category",
    scenarioId: "mixed-coca-cola-lockheed-tsla-fast-food",
    tags: [
      "category",
      "equity",
      "exact_identity",
      "identity",
      "mixed_slots",
      "name_resolution",
    ],
    turns: [
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "equity:ko",
              chainId: 1,
              productSymbol: "KOon",
              slotId: "slot_coca_cola",
              targetBps: 2500,
            },
            {
              canonicalExposureId: "equity:lmt",
              chainId: 1,
              productSymbol: "LMTon",
              slotId: "slot_lockheed",
              targetBps: 2500,
            },
            {
              canonicalExposureId: "equity:tsla",
              chainId: 1,
              productSymbol: "TSLAon",
              slotId: "slot_tsla",
              targetBps: 2500,
            },
            {
              canonicalExposureId: "equity:mcd",
              chainId: 1,
              productSymbol: "MCDon",
              slotId: "slot_fast_food",
              targetBps: 2500,
            },
          ],
          expectedPlan: {
            requiredSymbols: ["KO", "LMT", "TSLA"],
            targetBpsBySlotId: {
              slot_coca_cola: 2500,
              slot_fast_food: 2500,
              slot_lockheed: 2500,
              slot_tsla: 2500,
            },
          },
          forbidden: {
            symbols: ["GOOG", "GOOGL", "MLM"],
          },
        },
        userText:
          "Create a basket of Coca-Cola, Lockheed, TSLA, and 1 fast food stock.",
      },
    ],
  },
  {
    assetGraphFixtureId: "rwa-equity-identity",
    scenarioId: "confusing-indexed-equity-names",
    tags: [
      "equity",
      "exact_identity",
      "identity",
      "legal_name_normalization",
      "name_resolution",
      "ticker_mismatch",
    ],
    turns: [
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "equity:crm",
              chainId: 1,
              productSymbol: "CRMon",
              slotId: "slot_salesforce",
              targetBps: 1250,
            },
            {
              canonicalExposureId: "equity:xyz",
              chainId: 1,
              productSymbol: "XYZon",
              slotId: "slot_block",
              targetBps: 1250,
            },
            {
              canonicalExposureId: "equity:googl",
              chainId: 1,
              productSymbol: "GOOGLon",
              slotId: "slot_googl",
              targetBps: 1250,
            },
            {
              canonicalExposureId: "equity:ge",
              chainId: 1,
              productSymbol: "GEon",
              slotId: "slot_ge_aerospace",
              targetBps: 1250,
            },
            {
              canonicalExposureId: "equity:pg",
              chainId: 1,
              productSymbol: "PGon",
              slotId: "slot_procter_gamble",
              targetBps: 1250,
            },
            {
              canonicalExposureId: "equity:v",
              chainId: 1,
              productSymbol: "Von",
              slotId: "slot_visa",
              targetBps: 1250,
            },
            {
              canonicalExposureId: "equity:ma",
              chainId: 1,
              productSymbol: "MAon",
              slotId: "slot_mastercard",
              targetBps: 1250,
            },
            {
              canonicalExposureId: "equity:axp",
              chainId: 1,
              productSymbol: "AXPon",
              slotId: "slot_american_express",
              targetBps: 1250,
            },
          ],
          expectedPlan: {
            requiredSymbols: [
              "CRM",
              "XYZ",
              "GOOGL",
              "GE",
              "PG",
              "V",
              "MA",
              "AXP",
            ],
            targetBpsBySlotId: {
              slot_american_express: 1250,
              slot_block: 1250,
              slot_ge_aerospace: 1250,
              slot_googl: 1250,
              slot_mastercard: 1250,
              slot_procter_gamble: 1250,
              slot_salesforce: 1250,
              slot_visa: 1250,
            },
          },
          forbidden: {
            symbols: ["SQ", "GOOG"],
          },
        },
        userText:
          "Create an equal-weight basket of Salesforce, Block, GOOGL, GE Aerospace, Procter Gamble, Visa, Mastercard, and American Express.",
      },
    ],
  },
  {
    assetGraphFixtureId: "rwa-equity-categories",
    scenarioId: "one-auto-one-staple-one-fast-food",
    tags: ["category", "equity", "precision"],
    turns: [
      {
        expectation: {
          expectedPlan: {
            requiredSymbols: [],
            targetBpsBySlotId: {
              slot_auto_stock: 3334,
              slot_consumer_staple: 3333,
              slot_fast_food: 3333,
            },
          },
          forbidden: {
            symbols: ["TSLA"],
          },
        },
        userText:
          "Create a basket with 1 automobile stock, 1 consumer staple, and 1 fast food stock.",
      },
    ],
  },
  {
    assetGraphFixtureId: "rwa-equity-categories",
    scenarioId: "two-semis-two-autos",
    tags: ["category", "equity", "multi_binding", "optimizer"],
    turns: [
      {
        expectation: {
          expectedBindings: [
            {
              canonicalExposureId: "equity:nvda",
              chainId: 1,
              productSymbol: "NVDAon",
              slotId: "slot_2_semiconductor_stocks",
              targetBps: 2500,
            },
            {
              canonicalExposureId: "equity:amd",
              chainId: 1,
              productSymbol: "AMDon",
              slotId: "slot_2_semiconductor_stocks",
              targetBps: 2500,
            },
            {
              canonicalExposureId: "equity:tsla",
              chainId: 1,
              productSymbol: "TSLAon",
              slotId: "slot_2_automobile_stocks",
              targetBps: 2500,
            },
            {
              canonicalExposureId: "equity:tm",
              chainId: 1,
              productSymbol: "TMon",
              slotId: "slot_2_automobile_stocks",
              targetBps: 2500,
            },
          ],
          expectedPlan: {
            requiredSymbols: [],
            targetBpsBySlotId: {
              slot_2_automobile_stocks: 5000,
              slot_2_semiconductor_stocks: 5000,
            },
          },
        },
        userText:
          "Create a portfolio with 2 semiconductor stocks and 2 automobile stocks.",
      },
    ],
  },
] as const satisfies readonly PortfolioConstructionScenarioV2[];
